import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { api, Store, Staff, StaffRule } from '../../api';
import Modal from '../ui/Modal';
import Dialog from '../ui/Dialog';
import { useToast } from '../../contexts/ToastContext';

interface StaffRuleSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
}

const TYPE_LABEL = { pair: 'ペア', ng: '相性NG' } as const;

// スタッフ同士の組み合わせ（ペア・相性NG）の設定。
const StaffRuleSettingsTab: React.FC<StaffRuleSettingsTabProps> = ({ stores, myProfile, isSystemAdmin }) => {
  const { showToast } = useToast();
  const [rules, setRules] = useState<StaffRule[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [storeId, setStoreId] = useState('');
  const [editing, setEditing] = useState<StaffRule | null>(null);
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const availableStores = stores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id));
  useEffect(() => { if (!storeId && availableStores.length > 0) setStoreId(availableStores[0].id); }, [availableStores, storeId]);

  useEffect(() => {
    (async () => {
      try {
        const [r, st] = await Promise.all([api.getStaffRules(), api.getStaff()]);
        setRules(r); setStaffList(st);
      } catch (e) { showToast('データの取得に失敗しました。', 'error'); }
      finally { setIsLoading(false); }
    })();
  }, []);

  const storeStaff = staffList.filter(s => s.stores?.includes(storeId) && s.status !== 'resigned' && !s.isGuest);
  const nameOf = (id: string) => { const s = staffList.find(x => x.id === id); return s ? `${s.lastName} ${s.firstName}` : '（削除されたスタッフ）'; };
  const storeRules = rules.filter(r => r.storeIds.includes(storeId));

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.staffId || !editing.partnerId) { showToast('スタッフを2名選んでください。', 'error'); return; }
    if (editing.staffId === editing.partnerId) { showToast('別のスタッフを選んでください。', 'error'); return; }
    const dup = rules.find(r => r.id !== editing.id && r.storeIds.includes(storeId) && ((r.staffId === editing.staffId && r.partnerId === editing.partnerId) || (r.staffId === editing.partnerId && r.partnerId === editing.staffId)));
    if (dup) { showToast('この2名の組み合わせは登録済みです。', 'error'); return; }
    try {
      await api.saveStaffRules([editing]);
      setRules(prev => prev.some(r => r.id === editing.id) ? prev.map(r => r.id === editing.id ? editing : r) : [...prev, editing]);
      setEditing(null);
      showToast('保存しました。', 'success');
    } catch (e) { showToast('保存に失敗しました。', 'error'); }
  };

  const handleDelete = (rule: StaffRule) => setDialog({
    isOpen: true, title: '組み合わせの削除', message: `${TYPE_LABEL[rule.type]}「${nameOf(rule.staffId)}・${nameOf(rule.partnerId)}」を削除しますか？`,
    onConfirm: async () => {
      setDialog(prev => ({ ...prev, isOpen: false }));
      try { await api.deleteStaffRule(rule.id); setRules(prev => prev.filter(r => r.id !== rule.id)); showToast('削除しました。', 'success'); }
      catch (e) { showToast('削除に失敗しました。', 'error'); }
    }
  });

  const selectClass = 'w-full border border-slate-300 rounded-[4px] px-3 py-2 text-sm font-bold bg-white outline-none';

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Dialog {...dialog} type="confirm" isDestructive onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800 whitespace-nowrap">ペア・相性NG</h2>
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} aria-label="店舗" className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600 outline-none">
            {availableStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={() => setEditing({ id: `rule_${Date.now()}`, type: 'pair', staffId: '', partnerId: '', storeIds: storeId ? [storeId] : [], memo: '' })} className="bg-blue-600 text-white font-bold px-4 py-2 rounded-[4px] text-sm flex items-center gap-1.5 whitespace-nowrap"><Plus size={16} />組み合わせを追加</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
        <div className="border border-slate-200 rounded-[4px] p-3"><span className="font-bold text-blue-700">ペア</span>　1人目が入る日は、2人目も同じ時間帯に入れます。新人とトレーナーなどに使います。</div>
        <div className="border border-slate-200 rounded-[4px] p-3"><span className="font-bold text-rose-700">相性NG</span>　2人を同じ時間帯に入れません。</div>
      </div>
      <div className="bg-white border border-gray-200 rounded-[4px] overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs">
            <tr><th className="p-4 whitespace-nowrap">種類</th><th className="p-4 whitespace-nowrap">1人目</th><th className="p-4 whitespace-nowrap">2人目</th><th className="p-4 whitespace-nowrap">メモ</th><th className="p-4 whitespace-nowrap text-center">操作</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={20} />読み込んでいます。</td></tr>
            ) : storeRules.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400 font-bold text-xs">組み合わせはまだありません。</td></tr>
            ) : storeRules.map(r => (
              <tr key={r.id} data-testid={`rule-${r.id}`}>
                <td className="p-4 whitespace-nowrap"><span className={`text-xs font-bold rounded px-2 py-0.5 border ${r.type === 'pair' ? 'text-blue-700 bg-blue-50 border-blue-200' : 'text-rose-700 bg-rose-50 border-rose-200'}`}>{TYPE_LABEL[r.type]}</span></td>
                <td className="p-4 font-bold text-slate-800 whitespace-nowrap">{nameOf(r.staffId)}</td>
                <td className="p-4 font-bold text-slate-800 whitespace-nowrap">{nameOf(r.partnerId)}</td>
                <td className="p-4 text-slate-600">{r.memo}</td>
                <td className="p-4 text-center whitespace-nowrap">
                  <button onClick={() => setEditing({ ...r })} className="text-xs font-bold text-blue-600 hover:underline mr-3">編集</button>
                  <button onClick={() => handleDelete(r)} aria-label="削除" className="p-1.5 text-gray-400 hover:text-red-600 align-middle"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing && rules.some(r => r.id === editing.id) ? '組み合わせの編集' : '組み合わせの追加'} maxWidth="max-w-md">
        {editing && (
          <div className="space-y-5 pt-2" data-testid="rule-edit">
            <div>
              <p className="text-xs font-bold text-slate-500 mb-1.5">種類</p>
              <div className="grid grid-cols-2 gap-2">
                {(['pair', 'ng'] as const).map(t => (
                  <button key={t} type="button" onClick={() => setEditing({ ...editing, type: t })} className={`py-2 rounded-[4px] border text-sm font-bold ${editing.type === t ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-slate-300 text-slate-500'}`}>{TYPE_LABEL[t]}</button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="rule-a" className="text-xs font-bold text-slate-500 mb-1.5 block">{editing.type === 'pair' ? '1人目（例：新人）' : '1人目'}</label>
              <select id="rule-a" value={editing.staffId} onChange={(e) => setEditing({ ...editing, staffId: e.target.value })} className={selectClass}>
                <option value="">選択してください</option>
                {storeStaff.map(s => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="rule-b" className="text-xs font-bold text-slate-500 mb-1.5 block">{editing.type === 'pair' ? '2人目（例：トレーナー）' : '2人目'}</label>
              <select id="rule-b" value={editing.partnerId} onChange={(e) => setEditing({ ...editing, partnerId: e.target.value })} className={selectClass}>
                <option value="">選択してください</option>
                {storeStaff.filter(s => s.id !== editing.staffId).map(s => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="rule-memo" className="text-xs font-bold text-slate-500 mb-1.5 block">メモ（任意）</label>
              <input id="rule-memo" value={editing.memo || ''} onChange={(e) => setEditing({ ...editing, memo: e.target.value })} placeholder="例：新人研修中のため" className={selectClass} />
            </div>
            <div className="pt-4 flex justify-end gap-2 border-t">
              <button onClick={() => setEditing(null)} className="px-5 py-2 text-slate-500 font-bold">キャンセル</button>
              <button onClick={handleSave} className="px-7 py-2 bg-blue-600 text-white font-bold rounded-[4px]">保存</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StaffRuleSettingsTab;
